import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getApplicationFees,
  getFamilyDiscount
} from '../../../../api/enrollments/get-application-fees'
import { updateEnrollmentProposition } from '../../../../api/enrollments/update-enrollment-proposition'
import { EnrollmentProposition } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition'
import { EnrollmentPropositionInput } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import { PropositionPricingInput } from '../../../../lib/types/models/enrollment-proposition/proposition-pricing'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import EnrollmentPropositionSummary from '../../enrollments/enrollment-form/enrollment-proposition-summary'

export type PropositionReductionInput = Pick<
  EnrollmentPropositionInput,
  'pricing' | 'promoCode' | 'cseCode'
>

type Props = {
  id: number
  enrollmentId: number
  className?: string
  defaultValues: EnrollmentPropositionInput
  onCancel?: () => void
  onSuccess: (data: EnrollmentProposition) => void
}

const PropositionReductionForm: React.FC<Props> = ({
  id,
  enrollmentId,
  defaultValues,
  className,
  onSuccess
}) => {
  const [input, setInput] = useState(defaultValues)
  const { data: applicationFees } = useQuery({
    queryKey: ['enrollments-application-fees'],
    queryFn: getApplicationFees
  })

  const { data: familyDiscount } = useQuery({
    queryKey: ['enrollments-family-discount'],
    queryFn: getFamilyDiscount
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateEnrollmentProposition,
    onError: showAxiosError,
    onSuccess
  })

  const handleSubmit = async (data: PropositionReductionInput) => {
    await mutateAsync({
      id,
      enrollmentId,
      pricing: data.pricing,
      promoCode: data.promoCode
    })
  }

  const togglePricingFlag = (flag: keyof PropositionPricingInput) => {
    if (isPending) return
    const next: EnrollmentPropositionInput = {
      ...input,
      pricing: { ...input.pricing, [flag]: !input.pricing?.[flag] }
    }
    setInput(next)
    handleSubmit(next)
  }

  const hasCse = !!defaultValues.cseCode || !!input.cseCode

  return (
    <div>
      <div className={cn(className, 'flex flex-col gap-3')}>
        <div className="flex gap-2 items-center">
          <Checkbox
            checked={input.pricing?.includeApplicationFees}
            onChange={() => togglePricingFlag('includeApplicationFees')}
          />
          <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
            Frais de dossier
          </Typography>
          <Badge text={`${applicationFees} €`} />
        </div>

        <div className="flex gap-2 items-center">
          <Checkbox
            checked={input.pricing?.includeFamilyDiscount}
            onChange={() => togglePricingFlag('includeFamilyDiscount')}
          />
          <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
            Réduction famille
          </Typography>
          <Badge text={`${familyDiscount} €`} />
        </div>

        {!hasCse && (
          <EnrollmentPropositionSummary.PromoCode
            input={input}
            cseCodeHint="Pour un code CSE, utilisez « Code Partenaire CSE »"
            onInputChange={({ promoCode }) => {
              if (isPending) return
              setInput({ ...input, promoCode })
              handleSubmit({ ...input, promoCode })
            }}
            onClearPromoCode={() => {
              if (isPending) return
              setInput({ ...input, promoCode: null })
              handleSubmit({ ...input, promoCode: null })
            }}
          />
        )}
      </div>
    </div>
  )
}

export default PropositionReductionForm
